import { useQuery } from '@tanstack/react-query';
import { facebookActionTypesOptions, getReferencesOptions } from '../api';
import type { Option } from '../../../types/shared';
import { EXLUDED_ACTIONS } from '../constants';
import getActionLevelTooltip from '../utils/getActionLevelTooltip';
import { InfoOutlined } from '@mui/icons-material';
import { useMemo } from 'react';
import { useCurrentNetwork } from '../../../hooks';
import { useCurrentTasksFormStore } from './index';

interface IProps {
  ruleLevel: string;
  indexTask: number;
}

const useActions = ({ ruleLevel, indexTask }: IProps) => {
  const network = useCurrentNetwork();
  const tasksFormStore = useCurrentTasksFormStore();
  const selectedActions = tasksFormStore.getTask(indexTask)?.actions.map((act) => act.action);

  const { data: facebookActions, isLoading: facebookLoading } = useQuery({
    ...facebookActionTypesOptions,
    enabled: network === 'facebook',
    select: (data): Option[] => {
      return data
        .filter(({ name }) => !EXLUDED_ACTIONS.includes(name))
        .map(({ name, level }) => {
          const disabled = level?.toLowerCase() !== ruleLevel?.toLowerCase() && level !== 'all';
          return {
            value: name,
            label: name,
            disabled,
            tooltip: disabled ? getActionLevelTooltip(level) : undefined,
            icon: disabled ? <InfoOutlined color={'inherit'} sx={{ fontSize: 21, p: 0, m: 0 }} /> : undefined,
          };
        });
    },
  });

  const { data: googleActions, isLoading: googleLoading } = useQuery({
    ...getReferencesOptions('google'),
    enabled: network === 'google',
    select: (data): Option[] => {
      return data.actions.map(({ name, levels }) => {
        const disabled = !levels.some((lvl) => lvl.id === ruleLevel);
        return {
          value: name,
          label: name,
          disabled,
          tooltip: disabled ? getActionLevelTooltip(levels.map((lvl) => lvl.name).join(' ')) : undefined,
          icon: disabled ? <InfoOutlined color={'inherit'} sx={{ fontSize: 21, p: 0, m: 0 }} /> : undefined,
        };
      });
    },
  });

  const options = useMemo(() => {
    if (network === 'google' && !!googleActions) {
      //map again to disable already selected option in action dropdown
      return googleActions.map((item) => {
        const disabled = item.disabled || selectedActions?.includes(String(item.value));
        return {
          ...item,
          disabled,
        };
      });
    }
    if (network === 'facebook' && !!facebookActions) {
      //map again to disable already selected option in action dropdown
      return facebookActions.map((item) => {
        const disabled = item.disabled || selectedActions?.includes(String(item.value));
        return {
          ...item,
          disabled,
        };
      });
    }

    return [];
  }, [facebookActions, googleActions, network, selectedActions]);

  return { options, isLoading: facebookLoading || googleLoading };
};

export default useActions;
